import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';

export const Container = styled.div`
  padding: 24px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.SECONDARY};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${COLORS.BACKGROUND};
    color: ${COLORS.PRIMARY};
    transform: translateX(-2px);
  }

  svg {
    font-size: 16px;
  }
`;

export const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 24px;
  margin: 0;
  font-weight: 600;
  position: relative;
  padding-left: 16px;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 24px;
    width: 4px;
    background-color: ${COLORS.PRIMARY};
    border-radius: 2px;
  }
`;

export const Content = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 24px;
  height: calc(100vh - 150px);
  overflow-y: auto;
`;

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  background-color: ${COLORS.BACKGROUND_SECONDARY};
  height: 100%;
  gap: 24px;
  padding: 24px;
`;

export const ConfigurationSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 200px;
  max-height: 100%;
`;

export const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  text-transform: lowercase;
  margin: 0;
  padding: 12px;
  border: ${({ disabled }) => disabled ? '2px dashed #E0E0E0' : '2px dashed'};
  border-radius: 8px;
  text-align: center;
  color: ${({ type, disabled }) => {
    if (disabled) return '#E0E0E0';
    switch (type) {
      case 'grade':
        return '#00C853';
      case 'position':
        return '#2196F3';
      case 'functional':
        return '#FF9800';
      case 'organizational':
        return '#FF9800';
      case 'technical':
        return '#FF9800';
      default:
        return COLORS.SECONDARY;
    }
  }};
  border-color: ${({ type, disabled }) => {
    if (disabled) return '#E0E0E0';
    switch (type) {
      case 'grade':
        return '#00C853';
      case 'position':
        return '#2196F3';
      case 'functional':
        return '#FF9800';
      case 'organizational':
        return '#FF9800';
      case 'technical':
        return '#FF9800';
      default:
        return COLORS.BORDER;
    }
  }};
`;

export const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding-right: 8px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  /* Add padding to the top to ensure first element's border is visible */
  padding-top: 1px;
`;

export const OptionButton = styled.button`
  padding: 16px;
  border-radius: 12px;
  border: ${({ type, selected }) => {
    if (!selected) return '1px solid #E0E0E0';
    switch (type) {
      case 'grade':
        return '2px solid #00C853';
      case 'position':
        return '2px solid #2196F3';
      case 'functional':
      case 'organizational':
      case 'technical':
        return '2px solid #FF9800';
      default:
        return '2px solid #E0E0E0';
    }
  }};
  background-color: ${({ type, selected }) => {
    if (!selected) return 'white';
    switch (type) {
      case 'grade':
        return '#E8F5E9';
      case 'position':
        return '#E3F2FD';
      case 'functional':
      case 'organizational':
      case 'technical':
        return '#FFF3E0';
      default:
        return 'white';
    }
  }};
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? '600' : '500')};
  text-align: center;
  transition: all 0.2s ease;
  color: ${({ type, selected }) => {
    if (!selected) return '#666';
    switch (type) {
      case 'grade':
        return '#00C853';
      case 'position':
        return '#2196F3';
      case 'functional':
      case 'organizational':
      case 'technical':
        return '#FF9800';
      default:
        return '#666';
    }
  }};
  opacity: ${({ selected, disabled }) => {
    if (disabled) return 0.5;
    return 1;
  }};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const LegendSection = styled.div`
  margin-top: 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 32px;

  &:not(:last-child) {
    margin-bottom: 32px;
  }
`;

export const LegendTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  text-transform: lowercase;
  margin: 0;
  padding: 16px;
  border: 2px dashed;
  border-radius: 8px;
  text-align: center;
  color: ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
  border-color: ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
  margin-bottom: 24px;
`;

export const LegendGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  align-items: start;
`;

export const LegendItem = styled.div`
  display: contents;

  & > div:first-child {
    padding: 12px 24px;
    border-radius: 8px;
    border: 1px solid ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
    color: ${({ type }) => type === 'grade' ? '#00C853' : '#FF9800'};
    font-weight: 500;
    text-align: center;
    min-width: 150px;
    height: fit-content;
    align-self: start;
  }

  & > div:last-child {
    padding: 12px 24px;
    border-radius: 8px;
    background: ${({ type }) => type === 'grade' ? '#E8F5E9' : '#FFF3E0'};
    color: #666;
  }
`;

export const HeaderButton = styled.button`
  padding: 8px 16px;
  background-color: white;
  color: ${COLORS.PRIMARY};
  border: 1px solid ${COLORS.PRIMARY};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  
  &:hover {
    background-color: ${COLORS.PRIMARY};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  svg {
    font-size: 16px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

export const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 16px;
  margin-bottom: 0;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

export const TabButton = styled.button`
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  color: ${({ 'data-active': active }) => active ? COLORS.PRIMARY : COLORS.GRAY};
  border-bottom: 2px solid ${({ 'data-active': active }) => active ? COLORS.PRIMARY : 'transparent'};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${COLORS.PRIMARY};
  }
`;

export const RolesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  width: 100%;
  height: 100%;
`;

export const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  z-index: 1;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 40px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  position: relative;
  
  &:focus {
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
  }

  &::placeholder {
    color: #94A3B8;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94A3B8;
  width: 24px;
  height: 24px;
  pointer-events: none;
  opacity: 0.5;
`;

export const RoleCard = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  height: 200px;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f1f3f5;
    border-color: #bdbdbd;
  }
`;

export const RoleCardContent = styled.div`
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const RolesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  gap: 16px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
`;

export const RolesContent = styled.div`
  margin-top: 8px;
  flex: 1;
  padding: 24px 24px 24px 24px;
  overflow: auto;
  background-color: ${COLORS.BACKGROUND_SECONDARY};
`;

export const StructureWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .org-unit-type-select {
    margin-bottom: 24px;
  }

  .sections-container {
    display: flex;
    gap: 16px;
    flex: 1;
    min-height: 0;
  }
`; 